/**
 * The Agents SDK chat protocol, as this app speaks it.
 *
 * ── WHY THIS IS HAND-WRITTEN AND NOT `useAgentChat` ─────────────────────────
 * Not a preference. Three facts established elsewhere in this repository make
 * the shipped hook unusable here, and each of them is load-bearing:
 *
 *  1. **The SDK's own route is not reachable.** `apps/ai/src/index.ts` serves
 *     `routeAgentRequest` only on a request it builds itself, after consuming a
 *     ticket — reaching `/agents/trainer-agent/:name` from outside is a complete
 *     bypass of `D34` and is deliberately a 404. `useAgent`, which
 *     `useAgentChat` is built on, addresses exactly that path. There is no
 *     option to point it at `/chat?ticket=…`.
 *  2. **History arrives over HTTP, and that endpoint is not exposed either.**
 *     `useAgentChat` recovers messages by fetching the agent's `/get-messages`
 *     route. Same 404, same reason. Recorded in `docs/TRAPS.md` as part of
 *     `T-58`.
 *  3. **`T-58` itself.** A stream that finished while the phone was asleep is
 *     not an "active" stream, so the server never offers to resume it. The
 *     answer is still buffered and is still reachable — by ACKing the ORIGINAL
 *     request id, which the client must have remembered across the
 *     disconnection. That is a client-side obligation, and it is the common
 *     case rather than the rare one: long answers finish while a screen is
 *     locked.
 *
 * What the hook DOES get right is asserted against its shipped source in
 * `apps/ai/src/__tests__/pool/client-hook.test.ts`, and this module implements
 * the same two behaviours by hand — with (3) added.
 *
 * ── EVERY FRAME HERE IS SPOKEN BY A TEST AGAINST THE REAL WORKER ────────────
 * `apps/ai/src/__tests__/pool/support/chat-client.ts` sends and reads exactly
 * these frames over a real WebSocket to the real agent inside workerd, and
 * `resumable-streaming.test.ts` drives it 7/7. This file is that client, minus
 * the test harness. `__tests__/chat-protocol.test.ts` pins the type strings
 * against the SDK's own `CHAT_MESSAGE_TYPES` by reading them out of the
 * installed package, so a rename in the SDK fails here rather than becoming a
 * screen that connects and never receives anything.
 */
/**
 * The SDK's `CHAT_MESSAGE_TYPES`, restated.
 *
 * Restated rather than imported because `agents` is a workerd package: adding
 * it to the mobile bundle would pull `ai`, the Anthropic SDK and the Durable
 * Object machinery behind it, for eleven string constants. The import that
 * would be safe — a type-only one — cannot carry values.
 *
 * The cost of restating is drift, and drift here is SILENT: a client sending a
 * type string the server no longer recognises gets no error frame, because
 * there is no frame to send it on. That is what the drift test exists for.
 */
export const CHAT_FRAME = {
    /**
     * Client → server, and server → every OTHER client: start again from zero
     * (`D124`).
     *
     * The whole mechanism is the SDK's: the frame deletes the message table,
     * wipes the resumable-stream buffers so a pre-clear pending stream cannot
     * replay into the fresh conversation, aborts any turn in flight, and
     * broadcasts the clear to the other connections. Nothing stored is touched —
     * profile, training config, equipment, plan and measurements live in D1
     * behind the API and the agent reads them over RPC each turn, so the
     * transcript carries no data to lose.
     *
     * It travels in both directions, which is why it is the one frame this
     * client both sends and handles.
     */
    CHAT_CLEAR: "cf_agent_chat_clear",
    /** Server → client: the full persisted message list. */
    CHAT_MESSAGES: "cf_agent_chat_messages",
    /** Client → server: one turn. `init.body` is a JSON `{ messages }`. */
    USE_CHAT_REQUEST: "cf_agent_use_chat_request",
    /** Server → client: one UI-message-stream chunk, or the terminator. */
    USE_CHAT_RESPONSE: "cf_agent_use_chat_response",
    /** Server → client, unprompted in `onConnect`: an active stream is resumable. */
    STREAM_RESUMING: "cf_agent_stream_resuming",
    /** Client → server: replay the stream with this request id. */
    STREAM_RESUME_ACK: "cf_agent_stream_resume_ack",
    /** Client → server: is there anything to resume? */
    STREAM_RESUME_REQUEST: "cf_agent_stream_resume_request",
    /** Server → client: nothing ACTIVE to resume. NOT the same as nothing at all. */
    STREAM_RESUME_NONE: "cf_agent_stream_resume_none",
    /** Server → client: a turn is accepted but has not started streaming yet. */
    STREAM_PENDING: "cf_agent_stream_pending",
    /**
     * Server → client: a turn was interrupted and the SDK is recovering it.
     *
     * **Listed so it is named, and deliberately not handled** (`D134`). This
     * agent overrides `onChatRecovery` to return `{ continue: false }`, so no
     * continuation is ever scheduled and this frame — which `@cloudflare/ai-chat`
     * 0.11 broadcasts while an incident is live — describes a state the server is
     * about to leave. The turn's own frames carry every bit of progress this
     * screen renders; a "recovering…" indicator driven by this one would announce
     * a retry that is not coming.
     *
     * It is in `CHAT_FRAME` rather than only in a comment because the drift test
     * pins every value here against the SDK's own bytes, and because the frame
     * arriving at all is evidence the far end is alive — the dispatcher re-arms
     * `D127`'s watchdog on every frame before it looks at the type, so falling
     * through to `default:` is not the same as ignoring it.
     */
    CHAT_RECOVERING: "cf_agent_chat_recovering",
    /**
     * Client → server: the answer to a client-side tool call (`D122`).
     *
     * The one frame this client sends that is not a turn. `askUser` is declared
     * with no `execute`, which is the whole definition of a client-side tool: the
     * agent streams the call out and the turn sits there until an answer comes
     * back over this frame. `autoContinue` is what makes the SDK resume the turn
     * on arrival rather than merely record the output — measured against the real
     * agent in `apps/ai/src/__tests__/pool/ask-tool-roundtrip.test.ts`, which
     * pins this frame byte for byte.
     */
    TOOL_RESULT: "cf_agent_tool_result",
};
/**
 * The tool the trainer asks questions with, restated (`D123`).
 *
 * The name is declared in `@repo/agents` as `ASK_USER_TOOL_NAME`, and this app
 * cannot import that package — it is `portableAcrossRepos`, so rule 12 lets it
 * import `@repo/kernel` and nothing else, and nothing may import it here for
 * one string either. Same restatement, same guard shape, as `CHAT_FRAME` above.
 *
 * Drift here is silent in the ordinary way: a client filtering on a stale name
 * simply never builds a widget, and the screen shows an answer that asked
 * nothing.
 */
export const ASK_USER_TOOL = "askUser";
/**
 * The client's own declaration of {@link ASK_USER_TOOL}, in the SDK's wire
 * shape, sent on every turn.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * The agent SDK reads client-side tool declarations out of ONE body key,
 * `clientTools`, beside `messages`
 * (`@cloudflare/ai-chat/dist/index.js:246`, `:250`). Send the body without it
 * and `_lastClientTools` stays empty forever, and then:
 *
 *   - `clientResolvableToolNames()` is an empty set,
 *   - `partAwaitsClientInteraction()` is `false` for an `input-available`
 *     `tool-askUser` part (`agents/dist/chat/index.js:1795-1803`),
 *   - `_repairInterruptedToolsBeforeTurn()` — which runs before EVERY turn —
 *     flips that part to `output-error` and persists it.
 *
 * To a person: the widget appears, they type a message instead of tapping,
 * and the widget is dead. Tapping it afterwards loses too, because
 * `_applyToolResult` keeps the first terminal result. HTTP 200, a
 * correct-looking reply, the answer discarded, nothing red anywhere.
 *
 * ── THE SHAPE IS THE SDK'S, NOT THE AI SDK'S ───────────────────────────────
 * `ClientToolSchema` is `{ name, description?, parameters? }`, and
 * `parameters` is a JSON Schema — deliberately NOT the AI SDK's `inputSchema`,
 * because a Zod schema cannot cross a socket. A near-miss on either the key or
 * the field names reproduces the silence above exactly, which is why
 * `chat-protocol.test.ts` reads the key back out of the installed SDK.
 *
 * ── WHY NO `parameters` ────────────────────────────────────────────────────
 * Only `name` is load-bearing: `clientResolvableToolNames()` reads that field
 * and nothing else, and the server refuses on purpose to build its tool set
 * from this declaration (`apps/ai/src/tools/ask-user-tool.ts` — a JSON Schema
 * written by the far end of a socket must not widen what `D123` narrowed).
 * A schema restated here would therefore be a second copy of
 * `askUserInputSchema` that nothing reads and nothing checks — drift with no
 * symptom. The name and a description are the whole contract.
 */
export const ASK_USER_CLIENT_TOOL = {
    description: "Ask the person questions they answer by tapping options.",
    name: ASK_USER_TOOL,
};
/**
 * The tool the trainer hands a screen over with, restated (`D112`).
 *
 * ── WHY A TOOL AND NOT A PART ───────────────────────────────────────────────
 * `WireMessage` below carries text parts only, *by decision and not by
 * poverty*, so a trainer that has just built a plan draft cannot hand it over
 * as a typed `screen-link` part. It offers the screen the way it asks a
 * question: a tool declared with no `execute`, streamed out as a call, resolved
 * on this device by a person tapping, and answered over
 * {@link CHAT_FRAME.TOOL_RESULT}.
 *
 * Restated rather than imported for {@link ASK_USER_TOOL}'s reason — the name
 * is `OPEN_SCREEN_TOOL_NAME` in `apps/ai/src/tools/open-screen-tool.ts`, and
 * this app may import neither that Worker nor `@repo/agents` (rule 12).
 * `open-screen.ts` restates the SCREENS the same way and refuses everything
 * else; the server's schema is the one that binds the model.
 */
export const OPEN_SCREEN_TOOL = "openScreen";
/**
 * The client's own declaration of {@link OPEN_SCREEN_TOOL}, sent on every turn
 * beside {@link ASK_USER_CLIENT_TOOL}.
 *
 * Everything {@link ASK_USER_CLIENT_TOOL}'s header says applies here unchanged,
 * and the failure it describes is the one to fear: omit this from the
 * `clientTools` body key and `clientResolvableToolNames()` never learns the
 * name, `partAwaitsClientInteraction()` is `false` for the `input-available`
 * part, and `_repairInterruptedToolsBeforeTurn()` — which runs before EVERY
 * turn — flips it to `output-error` and persists it. To a person: the control
 * appears, they tap it, and nothing happens. HTTP 200, a correct-looking reply,
 * the tap discarded, nothing red anywhere. `_applyToolResult` keeps the first
 * terminal result, so there is no recovering it afterwards either.
 *
 * NO `parameters`, for the same reason as above: only `name` is read, and the
 * server refuses on purpose to build its tool set from a declaration written by
 * the far end of a socket. A JSON Schema restated here would be a second copy
 * of `openScreenInputSchema` that nothing reads and nothing checks.
 */
export const OPEN_SCREEN_CLIENT_TOOL = {
    description: "Offer the person a screen to open, which they tap to accept.",
    name: OPEN_SCREEN_TOOL,
};
/**
 * `resume_none`'s reasons. `idle` is the one that matters: it means "no active
 * stream", which is exactly the state a FINISHED answer leaves behind (`T-58`).
 */
export const STREAM_RESUME_NONE_IDLE = "idle";
/**
 * Parse one WebSocket message. Returns `null` for anything that is not a frame
 * — a binary message, malformed JSON, or a JSON value that is not an object.
 *
 * Returning `null` rather than throwing: a socket carrying one unparseable
 * message is not a broken conversation, and tearing the screen down over it
 * would turn a stray frame into a lost answer.
 */
export function parseFrame(data) {
    if (typeof data !== "string") {
        return null;
    }
    let value;
    try {
        value = JSON.parse(data);
    }
    catch {
        return null;
    }
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }
    const frame = value;
    return typeof frame.type === "string" ? value : null;
}
/**
 * The server's own id for the answer now arriving, or `null` (`T-118`).
 *
 * ── WHY THE CLIENT NEEDS IT, AND WHY IT IS ON THIS CHUNK ────────────────────
 * A draft is keyed with `localId()`, because the answer starts arriving before
 * anything is persisted and a bubble needs a key. Replayed under that id on the
 * next turn, the agent finds no row with it and INSERTS a second one beside its
 * own — so the answer comes back twice from `/chat/history`, and the transcript
 * grows a twin per turn. Measured against the live agent on 2026-09-01: two
 * rows, identical 232-character text.
 *
 * The SDK has two fallbacks and neither reaches this app. `reconcileAssistantIds`
 * matches on `JSON.stringify(sanitized.parts)` when ids differ — the server
 * stores a `state` field on the text part that a client never sends, so the
 * serialisations are never equal. And `cf_agent_chat_messages`, which does carry
 * the ids, is broadcast with the ORIGINATING connection excluded
 * (`_broadcastChatMessage(..., [connection.id])`) — so the one connection that
 * needs it is the one that never receives it. Both measured, the second by
 * dumping every frame of a live turn: `cf_agent_identity`, `cf_agent_mcp_servers`,
 * seven `cf_agent_use_chat_response`, and no `cf_agent_chat_messages` at all.
 *
 * What DOES arrive is this: the UI message stream opens with
 * `{"type":"start","messageId":"assistant_…"}`, on the same wire as the text and
 * before the first delta. It is the server's id, stated by the server, to the
 * connection that asked. Nothing has to be inferred.
 */
export function startMessageId(body) {
    if (body === undefined || body === "") {
        return null;
    }
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        return null;
    }
    // The array branch exists for the same reason `textOfChunk`'s does: the
    // server packs a segment of chunks into a JSON array once ten accumulate,
    // and a resumed stream replays through that path.
    const events = Array.isArray(parsed) ? parsed : [parsed];
    for (const event of events) {
        const chunk = (typeof event === "string" ? safeParse(event) : event);
        if (chunk !== null &&
            typeof chunk === "object" &&
            chunk.type === "start" &&
            typeof chunk.messageId === "string" &&
            chunk.messageId !== "") {
            return chunk.messageId;
        }
    }
    return null;
}
function safeParse(raw) {
    try {
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
}
/**
 * The text carried by one `cf_agent_use_chat_response` frame.
 *
 * ── THE WIRE FORMAT IS BARE JSON, NOT SSE ───────────────────────────────────
 * The same UI-message stream is `data:`-prefixed when it travels over HTTP; the
 * WebSocket transport carries the parsed events instead. A parser written for
 * the SSE form finds no text and reports an empty answer — which looks exactly
 * like a buffer that was never written. Measured and recorded in
 * `apps/ai/src/__tests__/pool/resumable-streaming.test.ts`.
 *
 * An ARRAY is accepted as well as an object: the server packs a segment of
 * chunks into a JSON array once ten have accumulated, and while the replay path
 * unpacks them before sending, handling both costs one branch and removes a
 * whole class of "the resumed answer is empty".
 */
export function textOfChunk(body) {
    if (body === undefined || body === "") {
        return "";
    }
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        return "";
    }
    return textOfEvent(parsed);
}
function textOfEvent(event) {
    if (Array.isArray(event)) {
        return event
            .map((item) => typeof item === "string" ? textOfChunk(item) : textOfEvent(item))
            .join("");
    }
    if (typeof event !== "object" || event === null) {
        return "";
    }
    const chunk = event;
    return chunk.type === "text-delta" && typeof chunk.delta === "string"
        ? chunk.delta
        : "";
}
/**
 * Does this chunk carry an error rather than text?
 *
 * ── AN ERROR CHUNK IS NOT THE TRUNCATION CASE, AND MUST NOT BE CONFLATED ────
 * The AI SDK emits `{ "type": "error" }` when generation itself failed, and the
 * screen shows that as a failure the user can retry. `T-57` — an object that
 * died mid-answer — produces NO error chunk at all: the truncated text is
 * replayed and marked `done`. The two states look nothing alike on the wire and
 * are handled in different places; see `answer-integrity.ts` for the one that
 * has no wire signal.
 */
export function isErrorChunk(body) {
    if (body === undefined || body === "") {
        return false;
    }
    try {
        const parsed = JSON.parse(body);
        return (typeof parsed === "object" &&
            parsed !== null &&
            parsed.type === "error");
    }
    catch {
        return false;
    }
}
/**
 * The tool events carried by one frame, in order.
 *
 * ── WHY `tool-input-available` AND NOT THE DELTAS ───────────────────────────
 * A tool call arrives as four chunks — `tool-input-start`, one or more
 * `tool-input-delta`, `tool-input-end`, then `tool-input-available` with the
 * WHOLE input already parsed into an object. Only the last one is read here.
 * Assembling the deltas would be re-implementing the SDK's own JSON accumulator
 * for the privilege of rendering a half-typed question, and a widget cannot be
 * shown half-built: two of its four options is not a question anyone can answer.
 *
 * The array branch is `textOfChunk`'s, for the same reason: the server packs a
 * segment of chunks into a JSON array once ten have accumulated.
 */
export function toolEventsOfChunk(body) {
    if (body === undefined || body === "") {
        return [];
    }
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        return [];
    }
    return toolEventsOfEvent(parsed);
}
function toolEventsOfEvent(event) {
    if (Array.isArray(event)) {
        return event.flatMap((item) => typeof item === "string"
            ? toolEventsOfChunk(item)
            : toolEventsOfEvent(item));
    }
    if (typeof event !== "object" || event === null) {
        return [];
    }
    const chunk = event;
    if ((chunk.type !== "tool-input-available" &&
        chunk.type !== "tool-output-available") ||
        typeof chunk.toolCallId !== "string" ||
        chunk.toolCallId === "") {
        return [];
    }
    return [
        {
            input: chunk.input,
            output: chunk.output,
            toolCallId: chunk.toolCallId,
            ...(typeof chunk.toolName === "string"
                ? { toolName: chunk.toolName }
                : {}),
            type: chunk.type,
        },
    ];
}
/**
 * The frame that answers a client-side tool call.
 *
 * `autoContinue: true` and the tool NAME are both required by the server and
 * neither is inferable there: the SDK matches the result to its part by
 * `toolCallId`, and continues the turn only when asked to. The shape is copied
 * from `useAgentChat`'s own send, which
 * `apps/ai/src/__tests__/pool/ask-tool-roundtrip.test.ts` proved reaches our
 * `onChatMessage`.
 */
export function toolResultFrame(options) {
    return {
        autoContinue: true,
        output: options.output,
        toolCallId: options.toolCallId,
        toolName: options.toolName ?? ASK_USER_TOOL,
        type: CHAT_FRAME.TOOL_RESULT,
    };
}
/**
 * The frame that asks for a turn.
 *
 * `init` mimics an HTTP request because the server parses it as one — the same
 * `onChatMessage` runs whether the turn arrived over a socket or a POST.
 *
 * The body carries exactly three keys the SDK knows by name — `messages`,
 * `clientTools` and `trigger` — and it destructures them out
 * (`@cloudflare/ai-chat/dist/index.js:246`); anything else is passed through
 * as a custom body, which this app does not use. `clientTools` is not
 * optional decoration: see {@link ASK_USER_CLIENT_TOOL} for what its absence
 * does to a question the person is looking at.
 *
 * BOTH client tools are declared on EVERY turn, including the turns neither is
 * used on. The declaration is what makes a call resolvable, and the repair pass
 * that would kill an unresolvable one runs before every turn — so a list that
 * varied with what the last answer happened to contain would leave a live
 * control dead on the next send.
 */
export function chatRequestFrame(options) {
    return {
        id: options.requestId,
        init: {
            body: JSON.stringify({
                clientTools: [ASK_USER_CLIENT_TOOL, OPEN_SCREEN_CLIENT_TOOL],
                messages: options.messages,
            }),
            method: "POST",
        },
        type: CHAT_FRAME.USE_CHAT_REQUEST,
    };
}
export function userMessage(id, text, metadata) {
    return {
        id,
        ...(metadata === undefined ? {} : { metadata }),
        parts: [{ text, type: "text" }],
        role: "user",
    };
}
//# sourceMappingURL=chat-protocol.js.map